import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { EditorOnly } from '../guards/Guards';

interface NavEvent { showSchedule?: boolean; raffleEnabled?: boolean; golfEnabled?: boolean; showMap?: boolean; status?: string; }

interface NavFlags {
  hasRsvp: boolean;
  showRsvp: boolean;
  showSchedule: boolean;
  showRaffle: boolean;
  showGolf: boolean;
  showMap: boolean;
  isSignedIn: boolean;
}

/** Primary nav links — shared by the desktop bar and the mobile menu so the two
 *  never drift. `onNavigate` lets the mobile menu close itself on a tap. */
function NavLinks({ flags, onNavigate }: { flags: NavFlags; onNavigate?: () => void }) {
  return (
    <>
      <NavLink to="/" end className="navlink" onClick={onNavigate}>Home</NavLink>
      {flags.showRsvp && (flags.hasRsvp ? (
        <NavLink to="/my-rsvp" className="navlink navlink--accent" onClick={onNavigate}>My RSVP</NavLink>
      ) : (
        <NavLink to="/rsvp" className="navlink navlink--accent" onClick={onNavigate}>RSVP</NavLink>
      ))}
      {flags.showSchedule && <NavLink to="/schedule" className="navlink" onClick={onNavigate}>Schedule</NavLink>}
      {flags.showGolf && <NavLink to="/golf" className="navlink" onClick={onNavigate}>Golf</NavLink>}
      {flags.showMap && <NavLink to="/maps" className="navlink" onClick={onNavigate}>Maps</NavLink>}
      {flags.isSignedIn && flags.showRaffle && <NavLink to="/raffle" className="navlink" onClick={onNavigate}>Raffle</NavLink>}
      {flags.isSignedIn && <NavLink to="/gallery" className="navlink" onClick={onNavigate}>Gallery</NavLink>}
      <EditorOnly>
        <NavLink to="/admin" className="navlink navlink--admin" onClick={onNavigate}>Admin</NavLink>
      </EditorOnly>
    </>
  );
}

/** Auth control — sign-in or signed-in name + sign-out. Reused in both layouts. */
function AuthControls({
  isSignedIn, displayName, signIn, signOut, onAction,
}: {
  isSignedIn: boolean; displayName: string | null;
  signIn: () => void; signOut: () => void; onAction?: () => void;
}) {
  if (isSignedIn) {
    return (
      <>
        <span className="site-header__user" title={displayName ?? ''}>
          {displayName ?? 'Signed in'}
        </span>
        <button onClick={() => { onAction?.(); signOut(); }} className="btn btn--ghost btn--sm">Sign out</button>
      </>
    );
  }
  return (
    <button onClick={() => { onAction?.(); signIn(); }} className="btn btn--ghost btn--sm">Employee sign-in</button>
  );
}

export function Header() {
  const { isSignedIn, displayName, signIn, signOut, getApiToken } = useAuth();
  const [evt, setEvt] = useState<NavEvent | null>(null);
  const [hasRsvp, setHasRsvp] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // ── public event flags (visibility toggles) ──────────────────────────────
  useEffect(() => {
    fetch('/api/public/event?year=2026')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setEvt(d))
      .catch(() => setEvt(null));
  }, []);

  // ── check whether the signed-in user has an existing RSVP ────────────────
  useEffect(() => {
    if (!isSignedIn) { setHasRsvp(false); return; }
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch('/api/me/rsvp', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { setHasRsvp(false); return; }
        const data = await res.json();
        setHasRsvp(!!data); // null body = no RSVP yet
      } catch { setHasRsvp(false); }
    })();
  }, [isSignedIn, getApiToken]);

  // Close the mobile menu on any route change (covers programmatic nav too).
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close on Escape while the menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Post-event (issue #57): once the year is wrapped, the season links go
  // away regardless of their individual toggles. Home + Gallery stay.
  const wrapped = evt?.status === 'completed';
  const flags: NavFlags = {
    hasRsvp,
    showRsvp: !wrapped,
    showSchedule: !wrapped && evt?.showSchedule !== false,
    showRaffle: !wrapped && evt?.raffleEnabled === true,
    showGolf: !wrapped && evt?.golfEnabled === true,
    showMap: !wrapped && evt?.showMap === true,
    isSignedIn,
  };

  return (
    <header className="site-header">
      <div className="site-header__inner">
        <NavLink to="/" className="site-header__brand" aria-label="WICnic home">
          <img
            src="/images/WIC_Red_616x252.png"
            alt="Western Industrial Contractors"
            className="site-header__logo"
          />
          <span className="site-header__divider" aria-hidden />
          <span className="site-header__title">WICnic</span>
        </NavLink>

        <nav className="site-header__nav" aria-label="Primary">
          <NavLinks flags={flags} />
        </nav>

        <div className="site-header__auth">
          <AuthControls isSignedIn={isSignedIn} displayName={displayName} signIn={signIn} signOut={signOut} />
        </div>

        <button
          type="button"
          className="nav-toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen(o => !o)}
        >
          <span className="nav-toggle__bar" data-open={menuOpen} />
          <span className="nav-toggle__bar" data-open={menuOpen} />
          <span className="nav-toggle__bar" data-open={menuOpen} />
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="mobile-menu__backdrop" onClick={() => setMenuOpen(false)} aria-hidden />
          <div className="mobile-menu" id="mobile-menu">
            <nav className="mobile-menu__nav" aria-label="Primary">
              <NavLinks flags={flags} onNavigate={() => setMenuOpen(false)} />
            </nav>
            <div className="mobile-menu__auth">
              <AuthControls
                isSignedIn={isSignedIn}
                displayName={displayName}
                signIn={signIn}
                signOut={signOut}
                onAction={() => setMenuOpen(false)}
              />
            </div>
          </div>
        </>
      )}

      <style>{`
        .site-header {
          position: sticky; top: 0; z-index: 50;
          background: var(--white);
          border-bottom: 1px solid var(--border);
          height: var(--nav-h);
        }
        .site-header__inner {
          max-width: 1400px; height: 100%; margin: 0 auto;
          padding: 0 var(--gutter);
          display: flex; align-items: center; gap: 2rem;
        }
        .site-header__brand { display: flex; align-items: center; gap: 1rem; text-decoration: none; color: var(--black); }
        .site-header__logo { height: 40px; width: auto; display: block; }
        .site-header__divider { height: 28px; width: 1px; background: var(--border); }
        .site-header__title {
          font-family: var(--font-display); font-weight: 700; font-size: 1.4rem;
          letter-spacing: 0.06em; text-transform: uppercase; color: var(--black);
        }
        .site-header__nav { display: flex; align-items: center; gap: 2rem; margin-left: auto; }
        .navlink {
          font-family: var(--font-display); font-weight: 500; font-size: 0.85rem;
          letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none;
          color: var(--charcoal); padding: 0.4rem 0;
          border-bottom: 2px solid transparent;
          transition: color var(--transition), border-color var(--transition);
        }
        .navlink:hover { color: var(--red); }
        .navlink.active { color: var(--red); border-bottom-color: var(--red); }
        .navlink--accent { color: var(--red); }
        .navlink--admin { color: var(--black); }
        .site-header__auth { display: flex; align-items: center; gap: 1rem; }
        .site-header__user {
          font-family: var(--font-sub); font-size: 0.85rem; color: var(--mid);
          max-width: 14ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .btn--sm { font-size: 0.75rem; padding: 0.5rem 1rem; letter-spacing: 0.12em; }

        /* ── Hamburger toggle — hidden on desktop ── */
        .nav-toggle {
          display: none;
          margin-left: auto;
          flex-direction: column; justify-content: center; gap: 5px;
          width: 44px; height: 44px; padding: 0;
          background: none; border: none; cursor: pointer;
        }
        .nav-toggle__bar {
          display: block; width: 24px; height: 2px; margin: 0 auto;
          background: var(--black);
          transition: transform var(--transition), opacity var(--transition);
        }
        .nav-toggle__bar[data-open="true"]:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .nav-toggle__bar[data-open="true"]:nth-child(2) { opacity: 0; }
        .nav-toggle__bar[data-open="true"]:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* ── Mobile dropdown menu ── */
        .mobile-menu__backdrop {
          position: fixed; inset: var(--nav-h) 0 0 0; z-index: 40;
          background: rgba(17,17,17,0.35);
        }
        .mobile-menu {
          position: absolute; top: 100%; left: 0; right: 0; z-index: 45;
          background: var(--white);
          border-bottom: 3px solid var(--red);
          box-shadow: var(--shadow);
          display: flex; flex-direction: column;
          padding: 0.5rem var(--gutter) 1.25rem;
        }
        .mobile-menu__nav { display: flex; flex-direction: column; }
        .mobile-menu__nav .navlink {
          padding: 0.9rem 0;
          border-bottom: 1px solid var(--border);
          border-top: none;
          font-size: 1rem;
        }
        .mobile-menu__nav .navlink.active { border-bottom-color: var(--red); }
        .mobile-menu__auth {
          display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
          margin-top: 1.25rem;
        }

        @media (max-width: 980px) {
          .site-header__nav { display: none; }
          .site-header__auth { display: none; }
          .nav-toggle { display: flex; }
        }
        @media (min-width: 981px) {
          /* Belt-and-suspenders: never show the mobile menu on desktop. */
          .mobile-menu, .mobile-menu__backdrop { display: none; }
        }
      `}</style>
    </header>
  );
}
